import React, { useState, useEffect } from "react";
import ApiService from "../../services/Api.service";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Alert from "../../components/Alert/Alert";
import Pagination from "../../components/Pagination/Pagination";
import StatusBadge from "../../utilities/StatusBadge";
import FilterBar from "../../components/FilterBar/FilterBar";
import Loader from "../../components/Loader/Loader";
import AuthService from "../../services/Auth.service";
import { Trash2, KeyRound } from "lucide-react";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import ResetPasswordModal from "./ResetPasswordModal";

export default function UserList() {
    const [Loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [postsPerPage, setPostsPerPage] = useState(20);
    const [headers, setHeaders] = useState([]);
    const [dropDownData, setDropDownData] = useState([]);
    const [roleDropdown, setRoleDropdown] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);

    const [pagination, setPagination] = useState({
        page: 1,
        size: 20,
        total: 0,
        total_pages: 0,
    });
    const [alertValue, setAlertValue] = useState({
        show: false,
        message: "",
        variant: "success",
        position: "middle",
    });
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);

    const validationSchema = yup.object().shape({
    });

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const fetchDetail = async (page = 1, size = postsPerPage, payload = null) => {
        try {
            setLoading(true);
            if (!payload) {
                payload = {
                    subprocess_id: "",
                    duration: "All",
                    status: "All",
                    disposition: "All",
                };
            }
            const res = await AuthService.getUserList(payload, { page, size });
            if (res.status === 200 && res.data) {
                setTableData(res.data.data);
                setPagination(res.data.pagination);
                setHeaders(res.data.header);
                setSelectedRowIndex(null);
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const fetchDropdownDetail = async () => {
        try {
            const subprocessRes = await ApiService.getSubprocesses();
            if (subprocessRes.status === 200) setDropDownData(subprocessRes.data);

            const userRoleRes = await AuthService.getUserRoles();
            if (userRoleRes.status === 200 && userRoleRes.data.user_roles)
                setRoleDropdown(userRoleRes.data.user_roles);


        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchDropdownDetail();
        fetchDetail();
    }, []);

    const handelSubmit = async (data) => {
        const payload = {
            subprocess_id: data.companyCode || "",
            status: data.status || "All",
            disposition: data.disposition || "All",
            duration: data.duration || "All",
        };
        ["age", "income", "product", "matrial"].forEach((key) => {
            payload[key] = Array.isArray(data[key]) ? data[key] : [];
        });

        fetchDetail(1, postsPerPage, payload);
    };


    const handleClearFilters = () => {
        reset({
            companyCode: "",
            duration: "",
            status: "All",
            disposition: "All",
        });
        fetchDetail();
    };

    const handlePageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setPostsPerPage(newSize);
        fetchDetail(1, newSize);
        setSelectedRowIndex(null);
    };

    const mistableTh = {
        backgroundColor:"transparent",
        color: "rgba(43, 58, 103, 1)",
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "0.1em",
        textAlign: "center",
    };

    const tableTdStyles = {
        fontFamily: "Nunito",
        fontSize: "12px",
        fontWeight: "700",
        lineHeight: "16px",
        letterSpacing: "0em",
        textAlign: "center",
        color: "#232122",
    };

    const filterConfigTable = [
        {
            name: "companyCode",
            label: "Company",
            type: "select",
            placeholder: "Select Template",
            options: dropDownData.map((item) => ({ label: item.name, value: item.id })),
        },
        {
            name: "role",
            label: "Role",
            type: "select",
            placeholder: "Select Role",
            options: roleDropdown.map((role) => ({
                label: role.charAt(0).toUpperCase() + role.slice(1),
                value: role,
            })),
            defaultValue: "",
        },
    ];
    const handleDeleteUser = async () => {
        if (!deleteTarget) return;
        try {
            const res = await AuthService.deleteUser(deleteTarget.id);
            if (res.status === 200) {
                setAlertValue({
                    show: true,
                    message: `${deleteTarget.name || "User"} deleted successfully!`,
                    variant: "success",
                    position: "middle",
                });
                fetchDetail(pagination.page, postsPerPage); // refresh
            }
        } catch (error) {
            console.error(error);
            setAlertValue({
                show: true,
                message: "Failed to delete user",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setShowDeleteModal(false);
            setDeleteTarget(null);
        }
    };


    const handleResetPassword = async (newPassword) => {
        if (!resetTarget) return;
        try {
            const res = await AuthService.resetPassword(resetTarget.id, { password: newPassword });
            if (res.status === 200) {
                setAlertValue({
                    show: true,
                    message: "Password reset successfully!",
                    variant: "success",
                    position: "middle",
                });
            }
        } catch (error) {
            console.error(error);
            setAlertValue({
                show: true,
                message: "Failed to reset password",
                variant: "danger",
                position: "middle",
            });
        } finally {
            setShowResetModal(false);
            setResetTarget(null);
        }
    };



    return (
        <> {alertValue.show && (
            <Alert
                variant={alertValue.variant}
                message={alertValue.message}
                dismissible={true}
                timeout={2500}
                position={alertValue.position}
                onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
            />
        )}
            <div className="insights-container">
                <>
                    {Loading && (
                        <div className="loader-container">
                            <Loader />
                        </div>
                    )}

                    <div className="main-contanier">
                        <FilterBar
                            control={control}
                            handleSubmit={handleSubmit}
                            onSubmit={handelSubmit}
                            onClearFilters={handleClearFilters}
                            filters={[]}
                            title={"User List"}
                            totalCount={pagination.total || 0}
                            tableId="table-id"
                            isDownload={false}
                        />

                        <div className="table-container dailymis-table" style={{ height: "400px" }}>
                            <table id="table-id" className="custom-table-container">
                                <thead
                                    className="table-head"
                                    style={{ position: "sticky", top: "0", zIndex: "1" }}
                                >

                                    <tr>
                                        <th style={mistableTh}>S.No</th>
                                        {headers.map((header, i) => (
                                            <th key={i} style={mistableTh}>
                                                {header.label}
                                            </th>
                                        ))}
                                        <th style={mistableTh}>Actions</th>
                                    </tr>


                                </thead>

                                <tbody>
                                    {tableData.length > 0 ? (
                                        tableData.map((item, index) => (
                                            <tr key={index} style={{ cursor: "pointer" }}>
                                                <td
                                                    style={{
                                                        ...tableTdStyles,
                                                        backgroundColor:
                                                            selectedRowIndex === index ? "#f9faff" : "transparent",
                                                    }}
                                                >
                                                    {index + 1}
                                                </td>
                                                {headers.map((header, i) => (
                                                    <td
                                                        key={i}
                                                        style={{
                                                            ...tableTdStyles,
                                                            backgroundColor:
                                                                selectedRowIndex === index ? "#f9faff" : "transparent",
                                                        }}
                                                    >
                                                        {header.value === "status" ? (
                                                            <StatusBadge status={item.status} />
                                                        ) : header.value === "disposition" ? (
                                                            <StatusBadge status={item.disposition} />
                                                        ) : (
                                                            item[header.value] ?? "--"
                                                        )}
                                                    </td>
                                                ))}
                                                <td style={tableTdStyles} >
                                                    <button
                                                        className="icon-btn delete-btn"
                                                        onClick={() => {
                                                            setDeleteTarget(item);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        title="Delete User"
                                                    >
                                                        <Trash2 size={18} color="#e63946" />
                                                    </button>
                                                    <button
                                                        className="icon-btn reset-btn"
                                                        onClick={() => {
                                                            setResetTarget(item);
                                                            setShowResetModal(true);
                                                        }}
                                                        title="Reset Password"
                                                        style={{ marginLeft: "8px" }}
                                                    >
                                                        <KeyRound size={18} color="#f4a261" />
                                                    </button>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={
                                                    headers.length + 1
                                                }
                                                style={tableTdStyles}
                                            >
                                                No data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            pagination={pagination}
                            postsPerPage={postsPerPage}
                            handlePageSizeChange={handlePageSizeChange}
                            fetchDetail={fetchDetail}
                        />
                    </div>

                </>
            </div>
            {showDeleteModal && (
                <ConfirmDeleteModal
                    onClose={() => {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                    }}
                    onConfirm={handleDeleteUser}
                    itemName={deleteTarget?.name || "this user"}
                />
            )}
            {showResetModal && (
                <ResetPasswordModal
                    onClose={() => {
                        setShowResetModal(false);
                        setResetTarget(null);
                    }}
                    onReset={handleResetPassword}
                />
            )}

        </>
    );
}

